import type { OtherVideo } from '@src/types.ts/story';

/**
 * Fields synced from the current video to its story.otherVideos / associatedVideos entry.
 */
export type VideoSyncFields = Pick<
  OtherVideo,
  | 'lastActionJson'
  | 'videoSource'
  | 'extraElementData'
  | 'karaokeData'
  | 'karaokeDataByLanguage'
  | 'transcriptionSnapshot'
  | 'transcriptionText'
  | 'scenes'
  | 'resourcesMetadata'
  | 'resourcesMetadataJson'
  | 'clipJson'
>;

/**
 * Sync saved video fields back to the matching entry in story.otherVideos
 * (or its associatedVideos). This keeps the in-memory story cache fresh so
 * that navigating back to Content Studio shows up-to-date data.
 */
export function syncVideoToStoryEntries(
  otherVideos: OtherVideo[],
  videoId: string | undefined,
  fields: VideoSyncFields,
): void {
  if (!videoId) return;

  const topIndex = otherVideos.findIndex(v => v.id === videoId);
  if (topIndex !== -1) {
    applyFields(otherVideos[topIndex], fields);
    return;
  }

  // Check associatedVideos of each top-level entry
  for (const otherVideo of otherVideos) {
    if (!otherVideo.associatedVideos) continue;
    const assocIndex = otherVideo.associatedVideos.findIndex(
      v => v.id === videoId,
    );
    if (assocIndex !== -1) {
      applyFields(otherVideo.associatedVideos[assocIndex], fields);
      return;
    }
  }
}

function applyFields(
  entry: Pick<OtherVideo, keyof VideoSyncFields>,
  fields: VideoSyncFields,
): void {
  entry.lastActionJson = fields.lastActionJson;
  entry.videoSource = fields.videoSource;
  entry.extraElementData = fields.extraElementData;
  entry.karaokeData = fields.karaokeData;
  entry.karaokeDataByLanguage = fields.karaokeDataByLanguage;
  entry.transcriptionSnapshot = fields.transcriptionSnapshot;
  entry.transcriptionText = fields.transcriptionText;
  entry.scenes = fields.scenes;
  entry.resourcesMetadata = fields.resourcesMetadata;
  entry.resourcesMetadataJson =
    fields.resourcesMetadataJson || fields.resourcesMetadata;
  entry.clipJson = fields.clipJson;
}
